'use client'

import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { MdAccessTime, MdSearch, MdWarning } from 'react-icons/md'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import ConfirmModal from '@/components/confirm-modal'
import SearchableSelect from '@/components/searchable-select'
import {
    getConfig,
    updateConfig,
    getBalances,
    getBalance,
    addEntry,
    getStatement,
    expireEntries,
    HrHourBankConfig,
    HrHourBank,
    HrHourBankEntry,
    HrHourBankStatement,
} from '@/lib/api/hr/hr-hour-bank-api'
import {
    getHrEmployees,
    HrEmployee,
} from '@/lib/api/hr/hr-employee-api'

const formatMinutes = (minutes: number): string => {
    const abs = Math.abs(minutes)
    const h = Math.floor(abs / 60)
    const m = abs % 60
    const sign = minutes < 0 ? '-' : minutes > 0 ? '+' : ''
    return `${sign}${h}h ${String(m).padStart(2, '0')}min`
}

const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('pt-BR')
}

const balanceClass = (minutes: number): string => {
    if (minutes > 0) return 'text-success'
    if (minutes < 0) return 'text-error'
    return 'text-base-content'
}

export default function HourBankPage() {
    const { canRead, canCreate, canUpdate } = useResourcePermissions('hr', 'hour_bank')

    // Configuracao
    const [config, setConfig] = useState<HrHourBankConfig | null>(null)
    const [configLoading, setConfigLoading] = useState(false)
    const [configEnabled, setConfigEnabled] = useState(false)
    const [configCompensationMonths, setConfigCompensationMonths] = useState(6)
    const [configMaxPositive, setConfigMaxPositive] = useState('')
    const [configMaxNegative, setConfigMaxNegative] = useState('')

    // Saldos
    const [balances, setBalances] = useState<HrHourBank[]>([])
    const [balancesTotal, setBalancesTotal] = useState(0)
    const [balancesPage, setBalancesPage] = useState(1)
    const [balancesSearch, setBalancesSearch] = useState('')
    const [balancesLoading, setBalancesLoading] = useState(false)

    // Lancamento Manual
    const [employees, setEmployees] = useState<HrEmployee[]>([])
    const [entryEmployeeId, setEntryEmployeeId] = useState('')
    const [entryType, setEntryType] = useState<'CREDIT' | 'DEBIT'>('CREDIT')
    const [entryHours, setEntryHours] = useState('')
    const [entryMinutes, setEntryMinutes] = useState('')
    const [entryDate, setEntryDate] = useState('')
    const [entryDescription, setEntryDescription] = useState('')
    const [entryLoading, setEntryLoading] = useState(false)

    // Extrato
    const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(null)
    const [statementData, setStatementData] = useState<HrHourBankStatement | null>(null)
    const [statementStartDate, setStatementStartDate] = useState('')
    const [statementEndDate, setStatementEndDate] = useState('')
    const [statementLoading, setStatementLoading] = useState(false)

    // Alertas
    const [expireLoading, setExpireLoading] = useState(false)
    const [expireConfirmOpen, setExpireConfirmOpen] = useState(false)

    // Carregar dados iniciais
    useEffect(() => {
        if (!canRead) return
        loadConfig()
        loadBalances()
        loadEmployees()

        // Definir datas padrão do extrato (último mês)
        const now = new Date()
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
        const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0)
        setStatementStartDate(startOfMonth.toISOString().split('T')[0])
        setStatementEndDate(endOfMonth.toISOString().split('T')[0])
    }, [canRead])

    const loadConfig = async () => {
        try {
            const res = await getConfig()
            if (res.success) {
                setConfig(res.result)
                setConfigEnabled(res.result.enabled)
                setConfigCompensationMonths(res.result.compensationMonths)
                setConfigMaxPositive(
                    res.result.maxPositiveBalance != null
                        ? String(Math.floor(res.result.maxPositiveBalance / 60))
                        : '',
                )
                setConfigMaxNegative(
                    res.result.maxNegativeBalance != null
                        ? String(Math.floor(res.result.maxNegativeBalance / 60))
                        : '',
                )
            }
        } catch {
            toast.error('Erro ao carregar configuração')
        }
    }

    const loadBalances = async (page = 1, search = '') => {
        setBalancesLoading(true)
        try {
            const res = await getBalances({ page, limit: 20, search: search || undefined })
            if (res.success) {
                const all = Array.isArray(res.result) ? res.result : []
                setBalances(all)
                setBalancesTotal(all.length)
            }
        } catch {
            toast.error('Erro ao carregar saldos')
        } finally {
            setBalancesLoading(false)
        }
    }

    const loadEmployees = async () => {
        try {
            const res = await getHrEmployees({ page: 1, limit: 500 })
            if (res.success) {
                setEmployees(Array.isArray(res.result) ? res.result : [])
            }
        } catch {
            // Silencioso
        }
    }

    const handleSaveConfig = async () => {
        setConfigLoading(true)
        try {
            const data = {
                enabled: configEnabled,
                compensationMonths: configCompensationMonths,
                maxPositiveBalance: configMaxPositive ? parseInt(configMaxPositive, 10) * 60 : null,
                maxNegativeBalance: configMaxNegative ? parseInt(configMaxNegative, 10) * 60 : null,
            }
            const res = await updateConfig(data)
            if (res.success) {
                toast.success('Configuração atualizada com sucesso')
                setConfig(res.result)
            } else {
                toast.error(res.error || 'Erro ao atualizar configuração')
            }
        } catch {
            toast.error('Erro ao atualizar configuração')
        } finally {
            setConfigLoading(false)
        }
    }

    const handleAddEntry = async () => {
        if (!entryEmployeeId || !entryDate) {
            toast.warning('Preencha todos os campos obrigatórios')
            return
        }

        const totalMinutes = (parseInt(entryHours || '0', 10) * 60) + parseInt(entryMinutes || '0', 10)
        if (totalMinutes < 1) {
            toast.warning('Informe pelo menos 1 minuto')
            return
        }

        setEntryLoading(true)
        try {
            const res = await addEntry({
                employeeId: parseInt(entryEmployeeId, 10),
                type: entryType,
                minutes: totalMinutes,
                date: entryDate,
                description: entryDescription || undefined,
            })
            if (res.success) {
                toast.success('Lançamento adicionado com sucesso')
                setEntryHours('')
                setEntryMinutes('')
                setEntryDate('')
                setEntryDescription('')
                loadBalances(balancesPage, balancesSearch)
                if (selectedEmployeeId === parseInt(entryEmployeeId, 10)) {
                    handleLoadStatement()
                }
            } else {
                toast.error(res.error || 'Erro ao adicionar lançamento')
            }
        } catch {
            toast.error('Erro ao adicionar lançamento')
        } finally {
            setEntryLoading(false)
        }
    }

    const handleLoadStatement = async (empId?: number) => {
        const employeeId = empId ?? selectedEmployeeId
        if (!employeeId || !statementStartDate || !statementEndDate) return

        setStatementLoading(true)
        try {
            const res = await getStatement(employeeId, statementStartDate, statementEndDate)
            if (res.success) {
                setStatementData(res.result)
                setSelectedEmployeeId(employeeId)
            } else {
                toast.error(res.error || 'Erro ao carregar extrato')
            }
        } catch {
            toast.error('Erro ao carregar extrato')
        } finally {
            setStatementLoading(false)
        }
    }

    const handleExpireEntries = async () => {
        setExpireLoading(true)
        try {
            const res = await expireEntries()
            if (res.success) {
                toast.success(`${res.result.count} entrada(s) expirada(s) processada(s)`)
                loadBalances(balancesPage, balancesSearch)
                setExpireConfirmOpen(false)
            } else {
                toast.error(res.error || 'Erro ao processar entradas expiradas')
            }
        } catch {
            toast.error('Erro ao processar entradas expiradas')
        } finally {
            setExpireLoading(false)
        }
    }

    const handleBalancesSearch = () => {
        loadBalances(1, balancesSearch)
    }

    const handleRowClick = (employeeId: number) => {
        setSelectedEmployeeId(employeeId)
        handleLoadStatement(employeeId)
    }

    const totalPages = Math.ceil(balancesTotal / 20)

    if (!canRead) {
        return (
            <div className="p-6">
                <div className="alert alert-warning">
                    Sem permissão para acessar o Banco de Horas.
                </div>
            </div>
        )
    }

    return (
        <div className="p-6 space-y-6">
            <div className="flex items-center gap-3">
                <MdAccessTime className="text-2xl text-secondary" />
                <h1 className="text-2xl font-bold">Banco de Horas</h1>
            </div>

            {/* Secao 1 - Configuracao */}
            {canUpdate && (
                <div className="card bg-base-100 shadow">
                    <div className="card-body">
                        <h2 className="card-title text-lg">Configuração</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text font-medium">Habilitado</span>
                                </label>
                                <input
                                    type="checkbox"
                                    className="toggle toggle-secondary mt-1"
                                    checked={configEnabled}
                                    onChange={(e) => setConfigEnabled(e.target.checked)}
                                />
                            </div>
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text font-medium">Meses de Compensação</span>
                                </label>
                                <input
                                    type="number"
                                    className="input input-bordered w-full"
                                    min={1}
                                    max={12}
                                    value={configCompensationMonths}
                                    onChange={(e) => setConfigCompensationMonths(parseInt(e.target.value, 10) || 6)}
                                />
                            </div>
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text font-medium">Limite Positivo (horas)</span>
                                </label>
                                <input
                                    type="number"
                                    className="input input-bordered w-full"
                                    placeholder="Sem limite"
                                    min={0}
                                    value={configMaxPositive}
                                    onChange={(e) => setConfigMaxPositive(e.target.value)}
                                />
                            </div>
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text font-medium">Limite Negativo (horas)</span>
                                </label>
                                <input
                                    type="number"
                                    className="input input-bordered w-full"
                                    placeholder="Sem limite"
                                    min={0}
                                    value={configMaxNegative}
                                    onChange={(e) => setConfigMaxNegative(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="card-actions justify-end mt-4">
                            <button
                                className="btn btn-secondary"
                                onClick={handleSaveConfig}
                                disabled={configLoading}
                            >
                                {configLoading ? <span className="loading loading-spinner loading-sm" /> : 'Salvar Configuração'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Secao 2 - Saldos */}
            <div className="card bg-base-100 shadow">
                <div className="card-body">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <h2 className="card-title text-lg">Saldos</h2>
                        <div className="join">
                            <input
                                type="text"
                                className="input input-bordered join-item w-64"
                                placeholder="Buscar funcionário..."
                                value={balancesSearch}
                                onChange={(e) => setBalancesSearch(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleBalancesSearch()}
                            />
                            <button className="btn btn-secondary join-item" onClick={handleBalancesSearch}>
                                <MdSearch className="text-lg" />
                            </button>
                        </div>
                    </div>

                    <div className="overflow-x-auto mt-4">
                        {balancesLoading ? (
                            <div className="flex justify-center py-8">
                                <span className="loading loading-spinner loading-lg" />
                            </div>
                        ) : (
                            <table className="table table-zebra">
                                <thead>
                                    <tr>
                                        <th>Nome</th>
                                        <th>Departamento</th>
                                        <th>Saldo</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {balances.length === 0 ? (
                                        <tr>
                                            <td colSpan={4} className="text-center py-8 text-base-content/60">
                                                Nenhum registro encontrado
                                            </td>
                                        </tr>
                                    ) : (
                                        balances.map((b) => (
                                            <tr
                                                key={b.id}
                                                className="cursor-pointer hover"
                                                onClick={() => handleRowClick(b.employeeId)}
                                            >
                                                <td className="font-medium">{b.employee?.name}</td>
                                                <td>{b.employee?.department || '-'}</td>
                                                <td className={`font-mono font-semibold ${balanceClass(b.balanceMinutes)}`}>
                                                    {formatMinutes(b.balanceMinutes)}
                                                </td>
                                                <td>
                                                    {b.balanceMinutes > 0 && (
                                                        <span className="badge badge-success badge-sm">Positivo</span>
                                                    )}
                                                    {b.balanceMinutes < 0 && (
                                                        <span className="badge badge-error badge-sm">Negativo</span>
                                                    )}
                                                    {b.balanceMinutes === 0 && (
                                                        <span className="badge badge-ghost badge-sm">Zerado</span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {totalPages > 1 && (
                        <div className="flex justify-center mt-4">
                            <div className="join">
                                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                                    <button
                                        key={p}
                                        className={`join-item btn btn-sm ${p === balancesPage ? 'btn-active' : ''}`}
                                        onClick={() => loadBalances(p, balancesSearch)}
                                    >
                                        {p}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Secao 3 - Lancamento Manual */}
            {canCreate && (
                <div className="card bg-base-100 shadow">
                    <div className="card-body">
                        <h2 className="card-title text-lg">Lançamento Manual</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text font-medium">Funcionário</span>
                                </label>
                                <SearchableSelect
                                    options={employees.map(emp => ({
                                        value: String(emp.id),
                                        label: emp.name,
                                        description: (emp as any).department || undefined,
                                    }))}
                                    value={entryEmployeeId}
                                    onChange={value => setEntryEmployeeId(value)}
                                    placeholder="Selecione um funcionário"
                                />
                            </div>
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text font-medium">Tipo</span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={entryType}
                                    onChange={(e) => setEntryType(e.target.value as 'CREDIT' | 'DEBIT')}
                                >
                                    <option value="CREDIT">Crédito</option>
                                    <option value="DEBIT">Débito</option>
                                </select>
                            </div>
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text font-medium">Data</span>
                                </label>
                                <input
                                    type="date"
                                    className="input input-bordered w-full"
                                    value={entryDate}
                                    onChange={(e) => setEntryDate(e.target.value)}
                                />
                            </div>
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text font-medium">Horas</span>
                                </label>
                                <input
                                    type="number"
                                    className="input input-bordered w-full"
                                    placeholder="0"
                                    min={0}
                                    value={entryHours}
                                    onChange={(e) => setEntryHours(e.target.value)}
                                />
                            </div>
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text font-medium">Minutos</span>
                                </label>
                                <input
                                    type="number"
                                    className="input input-bordered w-full"
                                    placeholder="0"
                                    min={0}
                                    max={59}
                                    value={entryMinutes}
                                    onChange={(e) => setEntryMinutes(e.target.value)}
                                />
                            </div>
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text font-medium">Descrição</span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    placeholder="Descrição do lançamento"
                                    value={entryDescription}
                                    onChange={(e) => setEntryDescription(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="card-actions justify-end mt-4">
                            <button
                                className="btn btn-secondary"
                                onClick={handleAddEntry}
                                disabled={entryLoading}
                            >
                                {entryLoading ? <span className="loading loading-spinner loading-sm" /> : 'Adicionar Lançamento'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Secao 4 - Extrato */}
            {selectedEmployeeId && (
                <div className="card bg-base-100 shadow">
                    <div className="card-body">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <div>
                                <h2 className="card-title text-lg">
                                    Extrato - {statementData?.employee?.name || ''}
                                </h2>
                                {statementData && (
                                    <p className={`text-sm font-mono font-semibold mt-1 ${balanceClass(statementData.currentBalance)}`}>
                                        Saldo atual: {formatMinutes(statementData.currentBalance)}
                                    </p>
                                )}
                            </div>
                            <div className="flex items-end gap-2">
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text text-xs">Início</span>
                                    </label>
                                    <input
                                        type="date"
                                        className="input input-bordered input-sm w-40"
                                        value={statementStartDate}
                                        onChange={(e) => setStatementStartDate(e.target.value)}
                                    />
                                </div>
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text text-xs">Fim</span>
                                    </label>
                                    <input
                                        type="date"
                                        className="input input-bordered input-sm w-40"
                                        value={statementEndDate}
                                        onChange={(e) => setStatementEndDate(e.target.value)}
                                    />
                                </div>
                                <button
                                    className="btn btn-secondary btn-sm"
                                    onClick={() => handleLoadStatement()}
                                    disabled={statementLoading}
                                >
                                    {statementLoading ? <span className="loading loading-spinner loading-xs" /> : 'Buscar'}
                                </button>
                            </div>
                        </div>

                        <div className="overflow-x-auto mt-4">
                            {statementLoading ? (
                                <div className="flex justify-center py-8">
                                    <span className="loading loading-spinner loading-lg" />
                                </div>
                            ) : (
                                <table className="table table-zebra table-sm">
                                    <thead>
                                        <tr>
                                            <th>Data</th>
                                            <th>Tipo</th>
                                            <th>Horas</th>
                                            <th>Descrição</th>
                                            <th>Vencimento</th>
                                            <th>Expirado</th>
                                            <th>Saldo</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {!statementData?.entries?.length ? (
                                            <tr>
                                                <td colSpan={7} className="text-center py-8 text-base-content/60">
                                                    Nenhum lançamento no período
                                                </td>
                                            </tr>
                                        ) : (
                                            statementData.entries.map((entry) => (
                                                <tr key={entry.id}>
                                                    <td>{formatDate(entry.date)}</td>
                                                    <td>
                                                        <span
                                                            className={`badge badge-sm ${
                                                                entry.type === 'CREDIT'
                                                                    ? 'badge-success'
                                                                    : 'badge-error'
                                                            }`}
                                                        >
                                                            {entry.type === 'CREDIT' ? 'Crédito' : 'Débito'}
                                                        </span>
                                                    </td>
                                                    <td className="font-mono">
                                                        {formatMinutes(
                                                            entry.type === 'CREDIT' ? entry.minutes : -entry.minutes,
                                                        )}
                                                    </td>
                                                    <td>{entry.description || '-'}</td>
                                                    <td>{entry.expiresAt ? formatDate(entry.expiresAt) : '-'}</td>
                                                    <td>
                                                        {entry.expired && (
                                                            <span className="badge badge-warning badge-sm">Expirado</span>
                                                        )}
                                                    </td>
                                                    <td className={`font-mono font-semibold ${balanceClass(entry.runningBalance || 0)}`}>
                                                        {formatMinutes(entry.runningBalance || 0)}
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Secao 5 - Alertas */}
            {canUpdate && (
                <div className="card bg-base-100 shadow">
                    <div className="card-body">
                        <h2 className="card-title text-lg">Alertas e Expiração</h2>
                        <div className="flex items-center gap-4 mt-4">
                            <div className="flex items-center gap-2 text-warning">
                                <MdWarning className="text-xl" />
                                <span className="text-sm">
                                    Entradas de crédito que ultrapassaram o prazo de compensação serão marcadas como expiradas
                                    e deverão ser pagas como hora extra na próxima folha.
                                </span>
                            </div>
                        </div>
                        <div className="card-actions justify-end mt-4">
                            <button
                                className="btn btn-warning"
                                onClick={() => setExpireConfirmOpen(true)}
                                disabled={expireLoading}
                            >
                                {expireLoading ? (
                                    <span className="loading loading-spinner loading-sm" />
                                ) : (
                                    'Processar Entradas Expiradas'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ConfirmModal
                isOpen={expireConfirmOpen}
                onClose={() => setExpireConfirmOpen(false)}
                onConfirm={handleExpireEntries}
                title="Processar Entradas Expiradas"
                message="Deseja processar todas as entradas de crédito vencidas? Os saldos serão ajustados e as horas deverão ser pagas como hora extra."
                loading={expireLoading}
                confirmButtonText="Processar"
                confirmButtonClass="btn-warning"
            />
        </div>
    )
}
